/**
 * AIF-03 — client-side label-file collection (§4 / story D4).
 *
 * The import wizard accepts three input modes — a single/multi `.txt` file
 * selection, a directory (via `webkitdirectory`), or a `.zip`. All three are
 * normalised here into the contract's `ImportLabelFile[]` shape
 * (`{ name, content }`) BEFORE anything is sent: the BE transport is a JSON
 * file array, not multipart, and does NOT unzip server-side (story §"BE
 * in-impl resolutions" #5). So the zip is expanded in the browser with
 * `fflate` (tiny — chosen over jszip per the brief).
 *
 * Collection rule (LOCKED): recursively collect every `*.txt` at ANY depth,
 * case-insensitively (`<code>/labels/` is not required). The `name` we keep is
 * the BASENAME only (the BE matches basename ↔ image filename) — directory /
 * zip paths are stripped so `24121/labels/DJI_0001.txt` and a bare
 * `DJI_0001.txt` both resolve identically.
 *
 * Caps mirror the contract: ≤2000 files, ≤1 MiB per file, ≤20 MiB total. We
 * surface a soft warning rather than throwing — over-cap sets are truncated by
 * the BE; the FE only flags them.
 */
import { unzipSync, strFromU8 } from "fflate";
import type { ImportLabelFile } from "@aeriosiq/types";

export const MAX_FILES = 2000;
export const MAX_FILE_BYTES = 1024 * 1024; // 1 MiB
export const MAX_TOTAL_BYTES = 20 * 1024 * 1024; // 20 MiB

export interface CollectResult {
  files: ImportLabelFile[];
  /** Human-readable soft warnings (over-cap, oversized files, etc.). */
  warnings: string[];
}

/** Lower-case `.txt` extension test (handles `.TXT`, `.Txt`). */
export function isTxt(name: string): boolean {
  return /\.txt$/i.test(name);
}

/** Strip any directory / zip path → bare basename. */
export function basename(path: string): string {
  // Normalise both separators; `webkitRelativePath` + zip both use `/`.
  const norm = path.replace(/\\/g, "/");
  const last = norm.split("/").pop() ?? norm;
  return last;
}

/**
 * Apply the contract caps + per-file size guard to an already-collected list.
 * Returns the (possibly truncated) list plus any soft warnings.
 */
function applyCaps(files: ImportLabelFile[]): CollectResult {
  const warnings: string[] = [];
  const enc = new TextEncoder();

  let kept = files.filter((f) => {
    const bytes = enc.encode(f.content).length;
    if (bytes > MAX_FILE_BYTES) {
      warnings.push(`${f.name} exceeds 1 MiB and was skipped.`);
      return false;
    }
    return true;
  });

  if (kept.length > MAX_FILES) {
    warnings.push(
      `${String(kept.length)} files detected — only the first ${String(
        MAX_FILES,
      )} will be sent (server cap).`,
    );
    kept = kept.slice(0, MAX_FILES);
  }

  const total = kept.reduce((sum, f) => sum + enc.encode(f.content).length, 0);
  if (total > MAX_TOTAL_BYTES) {
    warnings.push(
      "Total payload exceeds 20 MiB — the server may reject the request.",
    );
  }

  return { files: kept, warnings };
}

/** Read a `File`/`Blob` to an ArrayBuffer, with a FileReader fallback for
 *  environments (jsdom) where `Blob.prototype.arrayBuffer` is absent. */
async function readArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") {
    return await file.arrayBuffer();
  }
  return await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("FileReader failed"));
    };
    reader.readAsArrayBuffer(file);
  });
}

/** Read a single `File` to text, with a FileReader fallback for jsdom. */
async function readText(file: Blob): Promise<string> {
  if (typeof file.text === "function") {
    return await file.text();
  }
  const buf = await readArrayBuffer(file);
  return new TextDecoder().decode(buf);
}

/**
 * Collect `.txt` files from a flat / directory `FileList` (multi-file input or
 * `webkitdirectory`). Non-`.txt` entries are ignored. Recurses by virtue of
 * `webkitdirectory` flattening the tree into the FileList already.
 */
export async function collectFromFileList(
  fileList: FileList | File[],
): Promise<CollectResult> {
  const arr = Array.from(fileList);
  const txts = arr.filter((f) => {
    // Prefer the relative path (directory mode) so nested basenames survive.
    const rel =
      (f as File & { webkitRelativePath?: string }).webkitRelativePath || "";
    return isTxt(rel || f.name);
  });
  const out: ImportLabelFile[] = [];
  for (const f of txts) {
    const rel =
      (f as File & { webkitRelativePath?: string }).webkitRelativePath || "";
    out.push({ name: basename(rel || f.name), content: await readText(f) });
  }
  return applyCaps(out);
}

/**
 * Expand a `.zip` in the browser and collect every nested `*.txt`. Uses
 * `fflate`'s synchronous `unzipSync` (the sets are small — 134 files in the
 * worked example).
 */
export async function collectFromZip(zip: File): Promise<CollectResult> {
  const buf = new Uint8Array(await readArrayBuffer(zip));
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(buf);
  } catch {
    return { files: [], warnings: ["The selected file is not a valid .zip."] };
  }
  const out: ImportLabelFile[] = [];
  for (const [path, bytes] of Object.entries(entries)) {
    // fflate emits directory entries with a trailing slash + empty body.
    if (path.endsWith("/")) continue;
    if (!isTxt(path)) continue;
    out.push({ name: basename(path), content: strFromU8(bytes) });
  }
  return applyCaps(out);
}
